import React, { useState } from 'react';
import { 
  Upload, FileText, CheckCircle, Eye, AlertCircle, User, MapPin, 
  Loader2, X, Trash2, GraduationCap, Building2, BookOpen
} from 'lucide-react';
import { formFillApi } from '../../services/formFillApi'; 

const FormUploadAndReview = ({ formData, updateFormData, readOnly = false, targetColleges = [] }) => {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'review'
  const [selectedDoc, setSelectedDoc] = useState("");
  const [previewDoc, setPreviewDoc] = useState(null);
  
  // State for upload
  const [uploading, setUploading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [uploadError, setUploadError] = useState("");

  const docOptions = [
    { label: 'Passport Size Photo', key: 'profilePhoto', required: true },
    { label: 'Signature', key: 'signature', required: true },
    { label: 'Aadhar Card', key: 'aadharCard', required: true },
    { label: 'PAN Card', key: 'panCard', required: false },
    { label: 'Class 10 Marksheet', key: 'class10Marksheet', required: true },
    { label: 'Class 10 Certificate', key: 'class10Certificate', required: true },
    { label: 'Class 12 Marksheet', key: 'class12Marksheet', required: true },
    { label: 'Class 12 Certificate', key: 'class12Certificate', required: true },
    { label: 'Transfer Certificate', key: 'transferCertificate', required: false },
    { label: 'Migration Certificate', key: 'migrationCertificate', required: false },
    { label: 'Income Certificate', key: 'incomeCertificate', required: false },
    { label: 'Caste Certificate', key: 'casteCertificate', required: false },
    { label: 'Domicile Certificate', key: 'domicileCertificate', required: false },
  ];

  const handleFileUpload = async (e, docKey) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError("File size exceeds 5MB limit.");
      return;
    }

    setUploading(true);
    setUploadingDoc(docKey);
    setUploadError("");

    try {
      // Call API
      const response = await formFillApi.uploadDocument(file, docKey);
      
      if (response && response.success && response.url) {
        updateFormData('documents', { [docKey]: response.url });
      } else {
        setUploadError("Upload failed. Please try again.");
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadError(err.message || "Failed to upload document");
    } finally {
      setUploading(false);
      setUploadingDoc(null);
    }
  };

  const onDropdownFileChange = (e) => {
    if (!selectedDoc) return;
    handleFileUpload(e, selectedDoc);
  };

  const removeFile = (docKey) => {
    if (readOnly) return;
    if (window.confirm("Are you sure you want to remove this document?")) {
      updateFormData('documents', { [docKey]: '' });
    }
  };

  const renderValue = (val) => val ? String(val) : <span className="text-gray-400 italic">--</span>;

  // --- PREVIEW LOGIC ---
  const renderDocPreview = (docKey) => {
    const url = formData.documents[docKey];
    if (!url) return null;

    const isImage = url.toLowerCase().match(/\.(jpg|jpeg|png)$/) || url.includes('profile_photo') || url.includes('signature');
    const isPDF = url.toLowerCase().endsWith(".pdf") || url.includes('.pdf');

    return (
      <div className="mt-4 p-4 border border-green-200 rounded-lg bg-green-50 animate-fadeIn">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-green-800 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            File Uploaded Successfully
          </p>
          <div className="flex gap-3">
             <button
              onClick={() => setPreviewDoc(url)}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
            >
              <Eye className="w-3 h-3" /> Preview
            </button>
            {!readOnly && (
              <button
                onClick={() => removeFile(docKey)}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 font-medium"
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            )}
          </div>
        </div>
        
        {isImage && (
          <div className="relative group cursor-pointer w-32 h-32" onClick={() => setPreviewDoc(url)}>
             <img
              src={url}
              alt="Preview"
              className="w-full h-full object-cover rounded-md border-2 border-green-200 shadow-sm transition-transform group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-md" />
          </div>
        )}
        {(isPDF || !isImage) && (
          <div className="flex items-center gap-3 bg-white border border-green-200 rounded-md p-3 shadow-sm cursor-pointer hover:bg-gray-50" onClick={() => setPreviewDoc(url)}>
            <div className="bg-red-50 p-2 rounded">
              <FileText className="w-6 h-6 text-red-600" />
            </div>
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">Document File</span>
              <p className="text-xs text-gray-500">Click to view document</p>
            </div>
          </div>
        )}
      </div>
    );
  };

  // --- TAB 1: UPLOAD ---
  const renderUploadTab = () => (
    <div className="space-y-8 animate-slideIn">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <p className="font-semibold">Upload Guidelines:</p>
          <ul className="list-disc pl-4 mt-1 space-y-1 opacity-90">
            <li>Maximum file size: 5MB per document</li>
            <li>Allowed formats: JPG, JPEG, PNG, PDF</li>
            <li>Documents marked with * are mandatory</li>
          </ul>
        </div>
      </div>

      {uploadError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4" />
          {uploadError}
        </div>
      )}

      {/* Main Upload Area */}
      {!readOnly && (
        <div className="bg-white border-2 border-dashed border-blue-200 rounded-xl p-6 shadow-sm space-y-6">
          <div>
            <label className="block text-sm font-bold text-gray-800 mb-2">
              Select Document Type <span className="text-red-500">*</span>
            </label>
            <select
              value={selectedDoc}
              onChange={(e) => {
                setSelectedDoc(e.target.value);
                setUploadError("");
              }}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-800 transition-shadow"
              disabled={uploading}
            >
              <option value="">-- Choose document to upload --</option>
              {docOptions.map((doc) => (
                <option key={doc.key} value={doc.key}>
                  {doc.label} {doc.required ? '*' : '(Optional)'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-bold text-gray-800 mb-3">
              Action
            </label>
            {selectedDoc ? (
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label
                    htmlFor="doc-upload-input"
                    className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-white text-sm font-bold cursor-pointer transition-all transform active:scale-95 ${
                      uploading && uploadingDoc === selectedDoc
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg'
                    }`}
                  >
                    {uploading && uploadingDoc === selectedDoc ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        {formData.documents[selectedDoc] ? 'Change File' : 'Choose File'}
                      </>
                    )}
                  </label>
                  <input
                    id="doc-upload-input"
                    type="file"
                    className="hidden"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={onDropdownFileChange}
                    disabled={uploading}
                  />
                </div>

                {renderDocPreview(selectedDoc)}
              </div>
            ) : (
              <div className="p-8 bg-gray-50 border border-gray-200 rounded-lg flex flex-col items-center justify-center text-center text-gray-400">
                <Upload className="w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm font-medium">Please select a document type from the dropdown above to start uploading.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Grid */}
      <div className="space-y-4 pt-4 border-t border-gray-100">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-bold text-gray-800">Upload Summary</h4>
          <span className="text-xs font-semibold px-3 py-1 bg-gray-100 rounded-full text-gray-600">
            {Object.values(formData.documents).filter(Boolean).length - (formData.documents.additionalDocs?.length || 0)} / {docOptions.length}
          </span>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {docOptions.map((doc) => {
            const value = formData.documents[doc.key];
            const isUploading = uploading && uploadingDoc === doc.key;
            
            return (
              <div
                key={doc.key}
                onClick={() => !readOnly && setSelectedDoc(doc.key)}
                className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
                  selectedDoc === doc.key ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                } ${
                  value
                    ? "bg-green-50 border-green-200"
                    : isUploading
                    ? "bg-blue-50 border-blue-200"
                    : doc.required
                    ? "bg-white border-red-200"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {value ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : isUploading ? (
                      <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                    ) : (
                      <div className={`w-4 h-4 rounded-full border-2 ${doc.required ? 'border-red-300' : 'border-gray-300'}`} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-xs font-bold block truncate ${value ? 'text-green-800' : 'text-gray-700'}`}>
                      {doc.label} {doc.required && <span className="text-red-500">*</span>}
                    </span>
                    <p className={`text-[10px] mt-1 font-medium ${
                      value ? 'text-green-600' : 
                      isUploading ? 'text-blue-600' :
                      'text-gray-400'
                    }`}>
                      {isUploading ? 'Uploading...' : value ? 'Uploaded' : 'Pending'}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // --- TAB 2: REVIEW ---
  const renderReviewTab = () => (
    <div className="space-y-8 animate-slideIn">
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-6 rounded-xl shadow-lg">
        <h3 className="text-2xl font-bold mb-2">Application Review</h3>
        <p className="opacity-90 text-sm">
          Please verify all details carefully. Once submitted, you will proceed to the payment gateway.
        </p>
      </div>

      {/* Target Colleges Section */}
      {targetColleges && targetColleges.length > 0 && (
        <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex items-center gap-2 mb-6 border-b pb-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Building2 className="text-indigo-600 w-5 h-5" />
            </div>
            <h4 className="text-lg font-bold text-gray-800">Applied Institutions</h4>
          </div>
          <div className="flex flex-wrap gap-3">
            {targetColleges.map((col, idx) => (
              <div key={idx} className="bg-indigo-50 border border-indigo-100 text-indigo-900 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {col.collegeName || col.name}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Personal Details */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-6 border-b pb-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <User className="text-blue-600 w-5 h-5" />
          </div>
          <h4 className="text-lg font-bold text-gray-800">Personal Details</h4>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 text-sm">
          {Object.entries(formData.personalDetails || {}).map(([key, value]) => (
            <div key={key} className="group">
              <span className="block text-gray-500 capitalize text-xs mb-1 group-hover:text-blue-600 transition-colors">
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </span>
              <span className="font-semibold text-gray-900 text-sm break-words">
                {renderValue(value)}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Details */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-6 border-b pb-4">
          <div className="p-2 bg-purple-100 rounded-lg">
            <MapPin className="text-purple-600 w-5 h-5" />
          </div>
          <h4 className="text-lg font-bold text-gray-800">Contact Details</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
          <div className="space-y-4">
             <div>
                <span className="block text-gray-500 text-xs mb-1">Email Address</span>
                <span className="font-semibold text-gray-900">{renderValue(formData.contactDetails?.email)}</span>
             </div>
             <div>
                <span className="block text-gray-500 text-xs mb-1">Mobile Number</span>
                <span className="font-semibold text-gray-900">{renderValue(formData.contactDetails?.mobile)}</span>
             </div>
          </div>
          <div className="space-y-4">
             <div>
                <span className="block text-gray-500 text-xs mb-1">Permanent Address</span>
                <span className="font-semibold text-gray-900 block leading-relaxed">
                  {renderValue(formData.contactDetails?.permanentAddress?.addressLine1)},<br/>
                  {renderValue(formData.contactDetails?.permanentAddress?.city)}, {renderValue(formData.contactDetails?.permanentAddress?.state)}<br/>
                  PIN: {renderValue(formData.contactDetails?.permanentAddress?.pinCode)}
                </span>
             </div>
          </div>
        </div>
      </section>

      {/* Academic Details - ADDED SECTION */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-6 border-b pb-4">
          <div className="p-2 bg-orange-100 rounded-lg">
            <GraduationCap className="text-orange-600 w-5 h-5" />
          </div>
          <h4 className="text-lg font-bold text-gray-800">Academic Qualification</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
           {/* Class 10 */}
           <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <h5 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4"/> Class 10th</h5>
              <div className="grid grid-cols-2 gap-3">
                 <div>
                    <span className="text-xs text-gray-500 block">Board</span>
                    <span className="font-medium text-sm">{renderValue(formData.academicDetails?.class10?.boardUniversity)}</span>
                 </div>
                 <div>
                    <span className="text-xs text-gray-500 block">Percentage</span>
                    <span className="font-medium text-sm">{renderValue(formData.academicDetails?.class10?.percentage)}%</span>
                 </div>
                 <div className="col-span-2">
                    <span className="text-xs text-gray-500 block">School</span>
                    <span className="font-medium text-sm">{renderValue(formData.academicDetails?.class10?.schoolCollege)}</span>
                 </div>
              </div>
           </div>

           {/* Class 12 */}
           <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
              <h5 className="font-bold text-gray-800 mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4"/> Class 12th</h5>
              <div className="grid grid-cols-2 gap-3">
                 <div>
                    <span className="text-xs text-gray-500 block">Board</span>
                    <span className="font-medium text-sm">{renderValue(formData.academicDetails?.class12?.boardUniversity)}</span>
                 </div>
                 <div>
                    <span className="text-xs text-gray-500 block">Percentage</span>
                    <span className="font-medium text-sm">{renderValue(formData.academicDetails?.class12?.percentage)}%</span>
                 </div>
                 <div className="col-span-2">
                    <span className="text-xs text-gray-500 block">Stream</span>
                    <span className="font-medium text-sm">{renderValue(formData.academicDetails?.class12?.stream)}</span>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Documents Status */}
      <section className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-6 border-b pb-4">
          <div className="p-2 bg-green-100 rounded-lg">
            <FileText className="text-green-600 w-5 h-5" />
          </div>
          <h4 className="text-lg font-bold text-gray-800">Uploaded Documents Verification</h4>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-xs">
          {Object.entries(formData.documents || {}).map(([key, value]) => {
            if (key === 'additionalDocs') return null;
            const label = docOptions.find(d => d.key === key)?.label || key;
            return (
              <div
                key={key}
                className={`p-3 rounded-lg border flex items-center justify-between ${
                  value ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-100'
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  {value ? (
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  )}
                  <span className="text-gray-700 font-medium truncate" title={label}>{label}</span>
                </div>
                {value && (
                   <button onClick={() => setPreviewDoc(value)} className="text-blue-600 hover:text-blue-800">
                     <Eye className="w-3 h-3" />
                   </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <div className="flex items-start gap-3 p-4 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-200 mt-6">
        <input type="checkbox" id="declaration" className="w-5 h-5 text-blue-600 rounded mt-0.5 cursor-pointer" />
        <label htmlFor="declaration" className="text-sm flex-1 cursor-pointer leading-relaxed">
          I hereby declare that the information provided above is true and correct to the best of my knowledge. I understand that any discrepancy found later may lead to cancellation of my admission.
        </label>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tabs */}
      {!readOnly && (
        <div className="flex p-1 bg-gray-100 rounded-xl">
          <button
            className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === 'upload' 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
            }`}
            onClick={() => setActiveTab('upload')}
          >
            <Upload className="w-4 h-4" />
            1. Upload Documents
          </button>
          <button
            className={`flex-1 py-3 text-sm font-bold rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
              activeTab === 'review' 
                ? 'bg-white text-purple-600 shadow-sm' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
            }`}
            onClick={() => setActiveTab('review')}
          >
            <FileText className="w-4 h-4" />
            2. Review & Submit
          </button>
        </div>
      )}

      {/* Content */}
      {/* If ReadOnly, force show Review Tab logic, else show Active Tab */}
      {readOnly ? renderReviewTab() : (
        <>
          {activeTab === 'upload' && renderUploadTab()}
          {activeTab === 'review' && renderReviewTab()}
        </>
      )}

      {/* Modal Preview */}
      {previewDoc && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 animate-fadeIn" onClick={() => setPreviewDoc(null)}>
          <div className="relative max-w-5xl w-full bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-4 border-b">
               <h3 className="font-bold text-gray-800">Document Preview</h3>
               <button
                onClick={() => setPreviewDoc(null)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 bg-gray-50 flex items-center justify-center">
               {(previewDoc.toLowerCase().match(/\.(jpg|jpeg|png)$/) || previewDoc.includes('profile_photo') || previewDoc.includes('signature')) ? (
                <img src={previewDoc} alt="Preview" className="max-w-full h-auto max-h-full object-contain rounded shadow-lg" />
              ) : (
                <iframe src={previewDoc} className="w-full h-full min-h-[500px] border rounded shadow-sm bg-white" title="Document Preview" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormUploadAndReview;